import { useEffect, useRef, useState } from 'react';

const problems = [
  {
    icon: 'ri-user-unfollow-line',
    title: 'Missed Leads',
    description: 'Potential clients fall through the cracks due to slow response times, missed follow-ups, and no automated intake system.',
    accent: '#7C3AED',
  },
  {
    icon: 'ri-loop-right-line',
    title: 'Manual Tasks',
    description: 'Your team wastes hours on repetitive data entry, copy-pasting between tools, and administrative work that could be automated.',
    accent: '#14B8A6',
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Scheduling Friction',
    description: 'Booking appointments, coordinating availability, and sending reminders takes time and causes unnecessary back-and-forth.',
    accent: '#7C3AED',
  },
  {
    icon: 'ri-chat-off-line',
    title: 'Poor Follow-Up',
    description: 'Inconsistent communication after initial contact leads to lost conversions and a disconnected client experience.',
    accent: '#14B8A6',
  },
];

const ProblemSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const chaosTools = [
    { icon: 'ri-file-excel-line', label: 'Spreadsheet', top: '12%', left: '5%', color: '#22C55E', delay: '0s' },
    { icon: 'ri-mail-line', label: 'Email', top: '8%', left: '30%', color: '#F59E0B', delay: '0.3s' },
    { icon: 'ri-phone-line', label: 'Phone', top: '15%', right: '12%', color: '#EF4444', delay: '0.6s' },
    { icon: 'ri-sticky-note-line', label: 'Notes', top: '50%', left: '3%', color: '#8B5CF6', delay: '0.9s' },
    { icon: 'ri-calendar-2-line', label: 'Calendar', top: '60%', left: '35%', color: '#06B6D4', delay: '0.4s' },
    { icon: 'ri-chat-1-line', label: 'Chat', top: '55%', right: '8%', color: '#F97316', delay: '0.7s' },
    { icon: 'ri-database-line', label: 'CRM', top: '30%', right: '25%', color: '#14B8A6', delay: '0.2s' },
  ];

  return (
    <section id="problem" className="relative py-28 overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* Subtle background accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-violet/20 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="max-w-2xl mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-violet tracking-widest uppercase mb-4 block">
            The Problem
          </span>
          <h2 className="font-space font-semibold text-brand-text leading-tight mb-5" style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}>
            Disconnected Processes Are{' '}
            <span className="text-brand-violet">Slowing You Down</span>
          </h2>
          <p className="font-inter text-brand-steel leading-relaxed text-base">
            Most businesses rely on manual handoffs, scattered tools, inconsistent follow-up, and time-consuming admin work. These inefficiencies create missed opportunities, slower response times, and operational drag.
          </p>
        </div>

        {/* Visual chaos diagram */}
        <div
          className="relative rounded-2xl overflow-hidden mb-12 h-52"
          style={{
            background: 'linear-gradient(135deg, #0f0e2e 0%, #1E1B4B 60%, #2d1b69 100%)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.2s',
          }}
        >
          {/* Background image overlay */}
          <img
            src="https://public.readdy.ai/ai/img_res/80b6bb7e39d2503c86e41d6fd5bf4c67.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center opacity-10"
          />

          {/* Floating chaos tool chips */}
          {chaosTools.map((tool, i) => (
            <div
              key={i}
              className="absolute flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm"
              style={{
                top: tool.top,
                left: (tool as any).left,
                right: (tool as any).right,
                animation: `float ${3 + i * 0.4}s ease-in-out infinite`,
                animationDelay: tool.delay,
                opacity: visible ? 0.9 : 0,
                transition: `opacity 0.6s ease ${0.3 + i * 0.1}s`,
              }}
            >
              <i className={`${tool.icon} text-sm`} style={{ color: tool.color }} />
              <span className="font-inter text-xs text-white/60 whitespace-nowrap">{tool.label}</span>
            </div>
          ))}

          {/* Chaos arrows (SVG-free — use borders/pseudoelements) */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/8 border border-white/15 backdrop-blur-sm">
                <i className="ri-alert-line text-amber-400 text-lg" />
                <div className="text-left">
                  <p className="font-space font-semibold text-white text-sm">Your current state: scattered, manual, and reactive</p>
                  <p className="font-inter text-white/45 text-xs mt-0.5">Every tool isolated. Every task requiring human effort.</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-8 mt-4">
                {['Email', 'Spreadsheet', 'CRM', 'Calendar', 'Phone'].map((t, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    <span className="font-inter text-[10px] text-white/30">{t}</span>
                  </div>
                ))}
              </div>
              <p className="font-inter text-white/25 text-[10px] mt-2 tracking-widest uppercase">No connection. No automation. No system.</p>
            </div>
          </div>
        </div>

        {/* Problem cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {problems.map((problem, i) => (
            <div
              key={problem.title}
              className="group relative bg-white rounded-xl p-6 border border-brand-light hover:border-brand-violet/30 transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.6s ease ${0.3 + i * 0.1}s, transform 0.6s ease ${0.3 + i * 0.1}s, border-color 0.3s, background 0.3s`,
              }}
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-6 right-6 h-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: problem.accent }}
              />

              {/* Icon */}
              <div
                className="w-12 h-12 flex items-center justify-center rounded-lg mb-5"
                style={{ background: `${problem.accent}15` }}
              >
                <i className={`${problem.icon} text-xl`} style={{ color: problem.accent }} />
              </div>

              <h3 className="font-space font-semibold text-brand-text text-base mb-3">
                {problem.title}
              </h3>
              <p className="font-inter text-brand-steel text-sm leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom callout */}
        <div
          className="mt-16 p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1E1B4B, #2d1b69)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.6s',
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20glowing%20network%20connections%20dark%20background%20indigo%20violet%20digital%20pattern%20flowing%20lines%20technology%20minimal%20elegant%20no%20text&width=1200&height=300&seq=wwai-cta-bg-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="max-w-xl relative z-10">
            <p className="font-space font-semibold text-white text-lg leading-snug">
              Every day without automation is a day of lost efficiency and missed growth opportunities.
            </p>
            <p className="font-inter text-white/55 text-sm mt-2">
              The good news? It&apos;s entirely fixable — and faster than you think.
            </p>
          </div>
          <button
            onClick={() => document.querySelector('#solution')?.scrollIntoView({ behavior: 'smooth' })}
            className="whitespace-nowrap font-inter font-semibold text-sm text-brand-indigo px-6 py-3 rounded-lg bg-brand-teal hover:opacity-90 transition-opacity cursor-pointer flex items-center gap-2 relative z-10"
          >
            See the Solution
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
